import type { Translations } from '@/lib/i18n';

import type { Invoice } from './types';

export const PAGE_SIZE = 9;

// ─── Amounts ─────────────────────────────────────────────────────────────────

// Form string → API number, dropping blanks (used for optional amounts).
export function toOptionalAmount(val: string): number | undefined {
  if (val.trim() === '') return undefined;
  const n = Number(val);
  return Number.isFinite(n) ? n : undefined;
}

// API number → form string, blank when absent.
export function fromOptionalAmount(n: number | null | undefined): string {
  return n == null ? '' : String(n);
}

// ─── Due date ────────────────────────────────────────────────────────────────

// Default due date for a new invoice: 15 days from today, as "YYYY-MM-DD"
// (what the native `<input type="date">` expects).
export function defaultDueDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 15);
  return d.toISOString().slice(0, 10);
}

// ─── Phase ───────────────────────────────────────────────────────────────────
// The API already carries a real payment status (unpaid | pending_confirmation
// | paid). The only thing not on the wire is whether an unpaid invoice has
// been sent yet, so the display phase only adds `draft`/`sent` on top of it.
export type InvoicePhase = 'draft' | 'sent' | 'pending_confirmation' | 'paid';

export function invoicePhase(invoice: Invoice): InvoicePhase {
  if (invoice.status === 'paid') return 'paid';
  if (invoice.status === 'pending_confirmation') return 'pending_confirmation';
  return invoice.sent_at ? 'sent' : 'draft';
}

export function invoicePhaseLabel(phase: InvoicePhase, t: Translations): string {
  switch (phase) {
    case 'paid':
      return t.invoiceStatusPaid;
    case 'pending_confirmation':
      return t.invoiceStatusPendingConfirmation;
    case 'sent':
      return t.invoicePhaseSent;
    default:
      return t.invoicePhaseDraft;
  }
}

export function invoicePhaseColorClass(phase: InvoicePhase): string {
  switch (phase) {
    case 'paid':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'pending_confirmation':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'sent':
      return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400';
    default:
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  }
}

// Invoice timestamps (sent_at/paid_at/created_at) are full ISO datetimes,
// unlike `due_date`, which is a bare YYYY-MM-DD (see `formatDate` in
// `@/routes/jobs/helpers` for that one).
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
